import React, { useState, useEffect } from 'react';
import { 
  MapPin, CheckCircle, XCircle, Clock, Eye, Edit3, Trash2,
  Search, Filter, Plus, Save, X, AlertTriangle, Globe,
  Calendar, Users, TrendingUp, ChevronLeft, ChevronRight,
  ChevronsLeft, ChevronsRight, User
} from 'lucide-react';
import { useTheme } from '../arrangement/ThemeContext';

export const DispensaryManagement = ({ 
  API_URL, 
  pendingDispensaries, 
  setPendingDispensaries,
  dispensaryStats,
  setDispensaryStats,
  selectedDispensary,
  setSelectedDispensary,
  actionLoading,
  setActionLoading,
  handleApproveDispensary,
  handleRejectDispensary,
  loadAdminData
}) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [liveDispensaries, setLiveDispensaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterState, setFilterState] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  const { colors } = useTheme(); 
  
  const [sortOrder, setSortOrder] = useState('asc');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDispensaries, setTotalDispensaries] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrev, setHasPrev] = useState(false);

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDispensary, setEditingDispensary] = useState(null);
  const [editDispensaryData, setEditDispensaryData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    website: '',
    latitude: '',
    longitude: ''
  });

  // Add dispensary modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [addDispensaryData, setAddDispensaryData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    website: '',
    latitude: '',
    longitude: ''
  });

  // Load live dispensaries when tab switches
  useEffect(() => {
    if (activeTab === 'live') {
      loadLiveDispensaries();
    }
  }, [activeTab, currentPage, perPage, searchTerm, filterState, sortBy, sortOrder]);

  const loadLiveDispensaries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: perPage.toString(),
        search: searchTerm,
        state: filterState,
        sort_by: sortBy,
        sort_order: sortOrder
      });

      const response = await fetch(`${API_URL}/api/admin/dispensaries?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setLiveDispensaries(data.dispensaries || []);
        
        if (data.pagination) {
          setTotalPages(data.pagination.pages);
          setTotalDispensaries(data.pagination.total);
          setHasNext(data.pagination.has_next);
          setHasPrev(data.pagination.has_prev);
        }
      }
    } catch (error) {
      console.error('Failed to load live dispensaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditDispensary = (dispensary) => {
    setEditingDispensary(dispensary);
    setEditDispensaryData({
      name: dispensary.name || '',
      address: dispensary.address || '',
      city: dispensary.city || '',
      state: dispensary.state || '',
      postal_code: dispensary.postal_code || '',
      website: dispensary.website || '',
      latitude: dispensary.latitude?.toString() || '',
      longitude: dispensary.longitude?.toString() || ''
    });
    setShowEditModal(true);
  };

  const handleSaveEditedDispensary = async () => {
    if (!editingDispensary) return;

    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/dispensaries/${editingDispensary.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editDispensaryData)
      });

      if (response.ok) {
        const updatedDispensary = await response.json();
        
        setLiveDispensaries(prev => 
          prev.map(d => d.id === editingDispensary.id 
            ? updatedDispensary.dispensary
            : d
          )
        );
        setShowEditModal(false);
        setEditingDispensary(null);
      }
    } catch (error) {
      console.error('Failed to update dispensary:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteDispensary = async (dispensaryId) => {
    if (!window.confirm('Are you sure you want to delete this dispensary? This action cannot be undone.')) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/dispensaries/${dispensaryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setLiveDispensaries(prev => prev.filter(d => d.id !== dispensaryId));
      }
    } catch (error) {
      console.error('Failed to delete dispensary:', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddDispensary = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/dispensaries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(addDispensaryData)
      });

      if (response.ok) {
        await loadLiveDispensaries();
        setShowAddModal(false);
        setAddDispensaryData({
          name: '',
          address: '',
          city: '',
          state: '',
          postal_code: '',
          website: '',
          latitude: '',
          longitude: ''
        });
      }
    } catch (error) {
      console.error('Failed to add dispensary:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Dispensary Review Card Component
  const DispensaryReviewCard = ({ dispensary, onApprove, onReject, onViewDetails, actionLoading }) => {
    return (
      <div style={{
        background: colors.bgTertiary,
        borderRadius: '20px',
        border: '1px solid #333',
        padding: '24px',
        transition: 'all 0.3s ease'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '20px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '600', color: '#e5e5e5' }}>
                {dispensary.name}
              </h3>
              <span style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                PENDING
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '16px' }}>
              {dispensary.address && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <MapPin size={16} style={{ color: '#00d4aa' }} />
                  <span style={{ color: '#b3b3b3', fontSize: '14px' }}>
                    {dispensary.address}, {dispensary.city}, {dispensary.state} {dispensary.postal_code}
                  </span>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <User size={16} style={{ color: '#8b5cf6' }} />
                <span style={{ color: '#b3b3b3', fontSize: '14px' }}>
                  Submitted by: {dispensary.submitted_by_username}
                </span>
              </div>

              {dispensary.website && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Globe size={16} style={{ color: '#ef4444' }} />
                  <a href={dispensary.website} target="_blank" rel="noopener noreferrer" 
                     style={{ color: '#ef4444', fontSize: '14px', textDecoration: 'none' }}>
                    {dispensary.website}
                  </a>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={16} style={{ color: '#888' }} />
                <span style={{ color: '#888', fontSize: '14px' }}>
                  {new Date(dispensary.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>

            {(dispensary.latitude && dispensary.longitude) && (
              <div style={{
                background: '#2a2a2a',
                padding: '12px',
                borderRadius: '8px',
                fontSize: '14px',
                color: '#888'
              }}>
                Coordinates: {dispensary.latitude}, {dispensary.longitude}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '200px' }}>
            <button
              onClick={() => onViewDetails(dispensary)}
              style={{
                padding: '12px 20px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <Eye size={16} />
              Review Details
            </button>

            <button
              onClick={async () => {
                try {
                  await onApprove(dispensary.id);
                } catch (error) {
                  alert('An error occurred: ' + error.message);
                }
              }}
              disabled={actionLoading}
              style={{
                padding: '12px 20px',
                background: actionLoading ? '#666' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '500',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <CheckCircle size={16} />
              Approve
            </button>

            <button
              onClick={() => onReject(dispensary.id)}
              disabled={actionLoading}
              style={{
                padding: '12px 20px',
                background: actionLoading ? '#666' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '500',
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              <XCircle size={16} />
              Reject
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Stats Dashboard */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
          borderRadius: '16px',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <Clock size={20} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Pending Review</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700' }}>{dispensaryStats.pending_dispensaries || 0}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '16px',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <CheckCircle size={20} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>Total Active</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700' }}>{dispensaryStats.total_dispensaries || 0}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          borderRadius: '16px',
          padding: '20px',
          color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <TrendingUp size={20} />
            <span style={{ fontSize: '14px', fontWeight: '500' }}>This Week</span>
          </div>
          <div style={{ fontSize: '28px', fontWeight: '700' }}>{dispensaryStats.recent_submissions || 0}</div>
        </div>

        {activeTab === 'live' && (
          <div style={{
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            borderRadius: '16px',
            padding: '20px',
            color: 'white',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '120px'
          }}
          onClick={() => setShowAddModal(true)}
          >
            <div style={{ textAlign: 'center' }}>
              <Plus size={32} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: '16px', fontWeight: '600' }}>Add Dispensary</div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>Direct Admin Add</div>
            </div>
          </div>
        )}
      </div>

      {/* Tab Toggle */}
      <div style={{
        display: 'flex',
        gap: '8px',
        background: '#0f0f0f',
        padding: '6px',
        borderRadius: '12px',
        border: '1px solid #333',
        marginBottom: '24px'
      }}>
        <button
          onClick={() => {
            setActiveTab('pending');
            setCurrentPage(1);
          }}
          style={{
            flex: 1,
            textAlign: 'center',
            padding: '12px',
            background: activeTab === 'pending' 
              ? 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)' 
              : 'transparent',
            color: activeTab === 'pending' ? 'white' : '#888',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <Clock size={16} style={{ display: 'inline-block', marginRight: '8px' }} />
          Pending Approval ({pendingDispensaries.length})
        </button>

        <button
          onClick={() => {
            setActiveTab('live');
            setCurrentPage(1);
          }}
          style={{
            flex: 1,
            textAlign: 'center',
            padding: '12px',
            background: activeTab === 'live' 
              ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' 
              : 'transparent',
            color: activeTab === 'live' ? 'white' : '#888',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          <MapPin size={16} style={{ display: 'inline-block', marginRight: '8px' }} />
          Live Dispensaries ({totalDispensaries})
        </button>
      </div>

      {/* Search and Filters (for live dispensaries) */}
      {activeTab === 'live' && (
        <div style={{
          background: colors.bgTertiary,
          borderRadius: '16px',
          padding: '20px',
          border: '1px solid #333',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
              <Search size={20} style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#888'
              }} />
              <input
                type="text"
                placeholder="Search dispensaries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  paddingLeft: '48px',
                  paddingRight: '16px',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  border: '2px solid #333',
                  borderRadius: '12px',
                  fontSize: '14px',
                  background: '#2a2a2a',
                  color: '#e5e5e5',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <select
              value={filterState}
              onChange={(e) => setFilterState(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: '14px',
                background: '#2a2a2a',
                color: '#e5e5e5',
                cursor: 'pointer'
              }}
            >
              <option value="all">All States</option>
              <option value="CA">California</option>
              <option value="CO">Colorado</option>
              <option value="WA">Washington</option>
              <option value="OR">Oregon</option>
              <option value="NV">Nevada</option>
              {/* Add more states as needed */}
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '12px 16px',
                border: '2px solid #333',
                borderRadius: '12px',
                fontSize: '14px',
                background: '#2a2a2a',
                color: '#e5e5e5',
                cursor: 'pointer'
              }}
            >
              <option value="name">Sort by Name</option>
              <option value="city">Sort by City</option>
              <option value="state">Sort by State</option>
              <option value="created_at">Sort by Date</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              style={{
                padding: '12px',
                background: '#333',
                color: '#ccc',
                border: 'none',
                borderRadius: '12px',
                cursor: 'pointer'
              }}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      )}

      {/* Content based on active tab */}
      {activeTab === 'pending' ? (
        /* Pending Dispensaries */
        <div style={{ display: 'grid', gap: '20px' }}>
          {pendingDispensaries.map(dispensary => (
            <DispensaryReviewCard
              key={dispensary.id}
              dispensary={dispensary}
              onApprove={handleApproveDispensary}
              onReject={handleRejectDispensary}
              onViewDetails={setSelectedDispensary}
              actionLoading={actionLoading}
            />
          ))}
          
          {pendingDispensaries.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '64px 24px',
              background: colors.bgTertiary,
              borderRadius: '20px',
              border: '1px solid #333'
            }}>
              <MapPin size={48} style={{ color: '#666', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5', fontSize: '20px', fontWeight: '600' }}>
                No pending dispensaries
              </h3>
              <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>
                All dispensary submissions have been reviewed
              </p>
            </div>
          )}
        </div>
      ) : (
        /* Live Dispensaries */
        <div>
          <div style={{ display: 'grid', gap: '16px', marginBottom: '24px' }}>
            {liveDispensaries.map(dispensary => (
              <div
                key={dispensary.id}
                style={{
                  background: colors.bgTertiary,
                  borderRadius: '16px',
                  padding: '20px',
                  border: '1px solid #333',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', gap: '20px', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      margin: '0 0 8px 0',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#e5e5e5'
                    }}>
                      {dispensary.name}
                    </h4>
                    
                    <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <MapPin size={14} style={{ color: '#888' }} />
                        <span style={{ color: '#888', fontSize: '14px' }}>
                          {dispensary.city}, {dispensary.state}
                        </span>
                      </div>
                      
                      {dispensary.website && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Globe size={14} style={{ color: '#888' }} />
                          <a href={dispensary.website} target="_blank" rel="noopener noreferrer" 
                             style={{ color: '#ef4444', fontSize: '14px', textDecoration: 'none' }}>
                            Website
                          </a>
                        </div>
                      )}
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} style={{ color: '#888' }} />
                        <span style={{ color: '#888', fontSize: '14px' }}>
                          {new Date(dispensary.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {dispensary.address && (
                      <p style={{
                        margin: '0',
                        color: '#ccc',
                        fontSize: '14px',
                        lineHeight: '1.5'
                      }}>
                        {dispensary.address}, {dispensary.city}, {dispensary.state} {dispensary.postal_code}
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      onClick={() => handleEditDispensary(dispensary)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      <Edit3 size={16} />
                      Edit
                    </button>

                    <button
                      onClick={() => handleDeleteDispensary(dispensary.id)}
                      disabled={actionLoading}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 12px',
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        cursor: actionLoading ? 'not-allowed' : 'pointer',
                        opacity: actionLoading ? 0.6 : 1
                      }}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {liveDispensaries.length === 0 && !loading && (
              <div style={{
                textAlign: 'center',
                padding: '64px 24px',
                background: colors.bgTertiary,
                borderRadius: '20px',
                border: '1px solid #333'
              }}>
                <MapPin size={48} style={{ color: '#666', marginBottom: '16px' }} />
                <h3 style={{ margin: '0 0 8px 0', color: '#e5e5e5', fontSize: '20px', fontWeight: '600' }}>
                  No dispensaries found
                </h3>
                <p style={{ margin: 0, color: '#888', fontSize: '16px' }}>
                  {searchTerm || filterState !== 'all' 
                    ? 'Try adjusting your search or filter criteria'
                    : 'No dispensaries have been added yet'}
                </p>
              </div>
            )}

            {loading && (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#888'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  border: '3px solid #333',
                  borderTop: '3px solid #8b5cf6',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 16px'
                }} />
                Loading dispensaries...
              </div>
            )}
          </div>

          {/* Pagination for live dispensaries */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '24px',
              padding: '20px',
              background: colors.bgTertiary,
              borderRadius: '12px',
              border: '1px solid #333'
            }}>
              <div style={{ color: '#888', fontSize: '14px' }}>
                Showing {((currentPage - 1) * perPage) + 1}-{Math.min(currentPage * perPage, totalDispensaries)} of {totalDispensaries} dispensaries
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#888', fontSize: '14px' }}>Show:</span>
                <select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: '#2a2a2a',
                    color: '#e5e5e5'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                <option value={100}>100</option>
                </select>
              </div>
              
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={!hasPrev}
                  style={{
                    padding: '8px',
                    background: hasPrev ? '#333' : '#222',
                    color: hasPrev ? '#ccc' : '#555',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: hasPrev ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <ChevronsLeft size={16} />
                </button>
                
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={!hasPrev}
                  style={{
                    padding: '8px',
                    background: hasPrev ? '#333' : '#222',
                    color: hasPrev ? '#ccc' : '#555',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: hasPrev ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <ChevronLeft size={16} />
                </button>
                
                <div style={{ display: 'flex', gap: '2px', alignItems: 'center', margin: '0 8px' }}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        style={{
                          padding: '8px 12px',
                          background: pageNum === currentPage ? '#8b5cf6' : '#333',
                          color: pageNum === currentPage ? 'white' : '#ccc',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          minWidth: '36px'
                        }}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!hasNext}
                  style={{
                    padding: '8px',
                    background: hasNext ? '#333' : '#222',
                    color: hasNext ? '#ccc' : '#555',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: hasNext ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <ChevronRight size={16} />
                </button>
                
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={!hasNext}
                  style={{
                    padding: '8px',
                    background: hasNext ? '#333' : '#222',
                    color: hasNext ? '#ccc' : '#555',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: hasNext ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <ChevronsRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Dispensary Modal */}
      {showEditModal && editingDispensary && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: colors.bgTertiary,
            borderRadius: '20px',
            padding: '32px',
            border: '1px solid #333',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h3 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '700',
                color: '#e5e5e5'
              }}>
                Edit Dispensary: {editingDispensary.name}
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Edit Form */}
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Dispensary Name *
                </label>
                <input
                  type="text"
                  value={editDispensaryData.name}
                  onChange={(e) => setEditDispensaryData(prev => ({ ...prev, name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Address
                </label>
                <input
                  type="text"
                  value={editDispensaryData.address}
                  onChange={(e) => setEditDispensaryData(prev => ({ ...prev, address: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={editDispensaryData.city}
                    onChange={(e) => setEditDispensaryData(prev => ({ ...prev, city: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    State
                  </label>
                  <input
                    type="text"
                    value={editDispensaryData.state}
                    onChange={(e) => setEditDispensaryData(prev => ({ ...prev, state: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={editDispensaryData.postal_code}
                    onChange={(e) => setEditDispensaryData(prev => ({ ...prev, postal_code: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Website
                </label>
                <input
                  type="url"
                  value={editDispensaryData.website}
                  onChange={(e) => setEditDispensaryData(prev => ({ ...prev, website: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={editDispensaryData.latitude}
                    onChange={(e) => setEditDispensaryData(prev => ({ ...prev, latitude: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={editDispensaryData.longitude}
                    onChange={(e) => setEditDispensaryData(prev => ({ ...prev, longitude: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
              <button
                onClick={() => setShowEditModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: '#333',
                  color: '#ccc',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={handleSaveEditedDispensary}
                disabled={actionLoading || !editDispensaryData.name}
                style={{
                  flex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '16px',
                  background: (actionLoading || !editDispensaryData.name) ? '#555' : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: (actionLoading || !editDispensaryData.name) ? 'not-allowed' : 'pointer'
                }}
              >
                {actionLoading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={20} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Dispensary Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: colors.bgTertiary,
            borderRadius: '20px',
            padding: '32px',
            border: '1px solid #333',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h3 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '700',
                color: '#e5e5e5'
              }}>
                Add New Dispensary
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Add Form - Same structure as Edit Form */}
            <div style={{ display: 'grid', gap: '16px' }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Dispensary Name *
                </label>
                <input
                  type="text"
                  value={addDispensaryData.name}
                  onChange={(e) => setAddDispensaryData(prev => ({ ...prev, name: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Address
                </label>
                <input
                  type="text"
                  value={addDispensaryData.address}
                  onChange={(e) => setAddDispensaryData(prev => ({ ...prev, address: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    City
                  </label>
                  <input
                    type="text"
                    value={addDispensaryData.city}
                    onChange={(e) => setAddDispensaryData(prev => ({ ...prev, city: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    State
                  </label>
                  <input
                    type="text"
                    value={addDispensaryData.state}
                    onChange={(e) => setAddDispensaryData(prev => ({ ...prev, state: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={addDispensaryData.postal_code}
                    onChange={(e) => setAddDispensaryData(prev => ({ ...prev, postal_code: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#e5e5e5',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  Website
                </label>
                <input
                  type="url"
                  value={addDispensaryData.website}
                  onChange={(e) => setAddDispensaryData(prev => ({ ...prev, website: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#2a2a2a',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={addDispensaryData.latitude}
                    onChange={(e) => setAddDispensaryData(prev => ({ ...prev, latitude: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    color: '#e5e5e5',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}>
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={addDispensaryData.longitude}
                    onChange={(e) => setAddDispensaryData(prev => ({ ...prev, longitude: e.target.value }))}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '2px solid #333',
                      borderRadius: '8px',
                      color: '#e5e5e5',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: '#333',
                  color: '#ccc',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              
              <button
                onClick={handleAddDispensary}
                disabled={actionLoading || !addDispensaryData.name}
                style={{
                  flex: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '16px',
                  background: (actionLoading || !addDispensaryData.name) ? '#555' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: (actionLoading || !addDispensaryData.name) ? 'not-allowed' : 'pointer'
                }}
              >
                {actionLoading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus size={20} />
                    Add Dispensary
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};